// Statically resolves every JPEG under src/app/asset/<category>/* to a
// content-hashed URL so Vite includes them in the build with cache-busting.
const modules = import.meta.glob('/src/app/asset/**/*.{jpg,jpeg,png,webp}', {
  eager: true,
  import: 'default',
}) as Record<string, string>;

// Slugs cover both the new launch taxonomy (the seven main categories) and
// the legacy curated tiles. Adding new entries here is backward-compatible —
// existing references to 'hair' / 'cake' / etc. continue to resolve.
export type CategorySlug =
  | 'hair'
  | 'cake'
  | 'makeup'
  | 'nail'
  | 'fashion'
  | 'food'
  | 'event'
  | 'beauty'
  | 'beautyHair'
  | 'mediaPhotoAnimation'
  | 'artistsPerformance'
  | 'foodCatering'
  | 'pastry'
  | 'eventServices'
  | 'fashionStyle'
  | 'additionalServices'
  // Senegal (SN) market taxonomy — trades, event & home services.
  // These have no photo assets; the UI renders them as icon tiles.
  | 'plumber'
  | 'electrician'
  | 'mason'
  | 'carpenter'
  | 'photographer'
  | 'catering'
  | 'eventDecoration'
  | 'houseCleaning'
  | 'childcare'
  | 'homeCooking'
  | 'hairdresser'
  | 'makeupArtist'
  | 'manicurist';

// Asset folder names on disk (some contain underscores or ampersands).
// Map JS slugs to the on-disk folder, so we can pull images per category
// without renaming the asset folders.
const FOLDER_FOR_SLUG: Partial<Record<CategorySlug, string[]>> = {
  hair: ['hair'],
  cake: ['cake'],
  makeup: ['makeup'],
  nail: ['nail'],
  fashion: ['fashion'],
  food: ['food'],
  event: ['event'],
  beauty: ['beauty', 'makeup'],
  // Seven launch-taxonomy slugs — re-using the existing folders so we
  // honour the "use images from src/assets" rule without forcing extra
  // dependencies. Order matters: first folder with images wins for `pick`.
  beautyHair: ['hair', 'makeup', 'nail', 'beauty'],
  mediaPhotoAnimation: ['media_creation'],
  artistsPerformance: ['artiste&spectacles'],
  // Cuisine = savoury food only. Pastries moved to their own slug.
  foodCatering: ['food'],
  pastry: ['cake'],
  eventServices: ['event'],
  fashionStyle: ['fashion'],
  additionalServices: ['beauty', 'makeup'],
};

const folderImages: Record<string, string[]> = {};
for (const [path, url] of Object.entries(modules)) {
  // /src/app/asset/hair/hair1.jpg → folder = "hair"
  const segments = path.split('/asset/');
  if (segments.length < 2) continue;
  const folder = segments[1].split('/')[0];
  (folderImages[folder] ??= []).push(url);
}
// Stable sort by URL so output is deterministic between renders/builds.
for (const key of Object.keys(folderImages)) folderImages[key].sort();

function imagesFor(slug: CategorySlug): string[] {
  const folders = FOLDER_FOR_SLUG[slug];
  if (!folders) return [];
  const out: string[] = [];
  for (const f of folders) {
    const list = folderImages[f];
    if (list && list.length) out.push(...list);
  }
  return out;
}

export const CATEGORY_IMAGES = Object.fromEntries(
  (Object.keys(FOLDER_FOR_SLUG) as CategorySlug[]).map((slug) => [slug, imagesFor(slug)]),
) as Record<CategorySlug, string[]>;

export type CuratedCategory = {
  slug: CategorySlug;
  /** i18n key for the display name — see locales `categories.<key>` */
  i18nKey: string;
  /**
   * Search keyword used to map this curated tile to a backend Category by
   * substring match on `code` or `name` (see CategoryLandingPage).
   */
  searchTerm: string;
  /**
   * Cover image used on the home page tile. Empty string when the category
   * has no photo asset — the UI then falls back to a branded icon tile.
   */
  cover: string;
  /**
   * Optional i18n key (`categoryGroups.<key>`) for the section this tile
   * belongs to. Used to render grouped sections (e.g. Senegal's "Home &
   * Repair" / "Event" / "Home services"). Global categories leave it unset.
   */
  group?: string;
};

const pick = (slug: CategorySlug): string => {
  const list = CATEGORY_IMAGES[slug];
  return list && list.length ? list[0] : '';
};

/**
 * The seven launch-taxonomy main categories shown on the home page and
 * category landing. Each `searchTerm` is chosen to match the corresponding
 * backend Category code or name (see V16 migration & ReferenceDataSeeder).
 */
const MAIN_CATEGORIES: CuratedCategory[] = [
  {
    slug: 'beautyHair',
    i18nKey: 'categories.beautyHair',
    // Backend code stayed 'HAIR' for backward-compat — match by code.
    searchTerm: 'HAIR',
    cover: pick('beautyHair'),
  },
  {
    slug: 'mediaPhotoAnimation',
    i18nKey: 'categories.mediaPhotoAnimation',
    searchTerm: 'MEDIA_PHOTO_ANIMATION',
    cover: pick('mediaPhotoAnimation'),
  },
  {
    slug: 'artistsPerformance',
    i18nKey: 'categories.artistsPerformance',
    searchTerm: 'ARTISTS_PERFORMANCE',
    // Hand-picked cover — change the filename to swap. Falls back to the
    // first sorted image in the folder if the file is missing.
    cover:
      modules['/src/app/asset/artiste&spectacles/spectacle3.jpg']
      ?? pick('artistsPerformance'),
  },
  {
    slug: 'foodCatering',
    i18nKey: 'categories.foodCatering',
    // Backend code stayed 'CAKE' for backward-compat — match by code.
    // Despite the legacy name, this row now covers SAVOURY food only;
    // pastries live in the {@code pastry} slug below.
    searchTerm: 'CAKE',
    cover:
      modules['/src/app/asset/food/food5.jpg']
      ?? pick('foodCatering'),
  },
  {
    slug: 'pastry',
    i18nKey: 'categories.pastry',
    searchTerm: 'PASTRY',
    cover:
      modules['/src/app/asset/cake/cake3.jpg']
      ?? pick('pastry'),
  },
  {
    slug: 'eventServices',
    i18nKey: 'categories.eventServices',
    searchTerm: 'EVENT_SERVICES',
    cover: pick('eventServices'),
  },
  {
    slug: 'fashionStyle',
    i18nKey: 'categories.fashionStyle',
    searchTerm: 'FASHION_STYLE',
    cover: pick('fashionStyle'),
  },
  {
    slug: 'additionalServices',
    i18nKey: 'categories.additionalServices',
    searchTerm: 'ADDITIONAL_SERVICES',
    cover: pick('additionalServices'),
  },
];

/**
 * Legacy entries kept so already-published links (`/category/hair`,
 * `/category/cake`, ...) and code that imports old slugs continue to work.
 * They are NOT rendered on the home page (see {@link getFeaturedCategories}).
 */
const LEGACY_CATEGORIES: CuratedCategory[] = [
  { slug: 'hair', i18nKey: 'categories.hair', searchTerm: 'HAIR', cover: pick('hair') },
  { slug: 'makeup', i18nKey: 'categories.makeup', searchTerm: 'makeup', cover: pick('makeup') },
  { slug: 'nail', i18nKey: 'categories.nail', searchTerm: 'nail', cover: pick('nail') },
  { slug: 'cake', i18nKey: 'categories.cake', searchTerm: 'CAKE', cover: pick('cake') },
  { slug: 'food', i18nKey: 'categories.food', searchTerm: 'CAKE', cover: pick('food') },
  { slug: 'fashion', i18nKey: 'categories.fashion', searchTerm: 'FASHION_STYLE', cover: pick('fashion') },
  { slug: 'event', i18nKey: 'categories.event', searchTerm: 'EVENT_SERVICES', cover: pick('event') },
  { slug: 'beauty', i18nKey: 'categories.beauty', searchTerm: 'HAIR', cover: pick('beauty') },
];

/**
 * Local Senegal photo assets — real prestataires shot in context (plumber
 * under a sink, electrician with cables, etc.). Files live in
 * {@code src/app/asset/senegalbild/} and are imported via the same Vite
 * glob as the European folders, so they're hashed + cached normally.
 * Exported only for the SenegalHero collage; CuratedCategory entries
 * reference these slots directly via the {@link cover} property.
 */
export const SENEGAL_PHOTOS = {
  // A. Home & Repair
  plumber: specificImage('senegalbild', '6.jpg'),
  electrician: specificImage('senegalbild', '7.jpg'),
  mason: specificImage('senegalbild', '1.jpg'),
  carpenter: specificImage('senegalbild', '3.jpg'),
  // B. Event services
  photographer: specificImage('senegalbild', '10.jpg'),
  catering: specificImage('senegalbild', '12.jpg'),
  eventDecoration: specificImage('senegalbild', '11.jpg'),
  // C. Home services
  houseCleaning: specificImage('senegalbild', '8.jpg'),
  childcare: specificImage('senegalbild', '13.jpg'),
  homeCooking: specificImage('senegalbild', '14.jpg'),
  // At-home beauty — dedicated Senegal-shot photos in `senegalbild/`.
  hairdresser: specificImage('senegalbild', 'braids.jpg'),
  makeupArtist: specificImage('senegalbild', 'makeup.jpg'),
  manicurist: specificImage('senegalbild', 'Beauty.jpg'),
};

/**
 * Senegal (SN) market taxonomy. When the visitor's selected country is
 * Senegal these REPLACE the European launch categories everywhere the UI
 * lists featured categories (home grid, nav menu, footer, search filter).
 *
 * `searchTerm` is the exact backend Category code (see ReferenceDataSeeder's
 * `SN_*` rows) so `CategoryLandingPage` resolves the right backend row.
 * Every category ships with a real Senegal photo so the landing page
 * never falls back to icon-only tiles for the local market.
 */
const SENEGAL_CATEGORIES: CuratedCategory[] = [
  // A. Home & Repair services
  { slug: 'plumber', i18nKey: 'categories.plumber', searchTerm: 'SN_PLUMBER', cover: SENEGAL_PHOTOS.plumber, group: 'categoryGroups.homeRepair' },
  { slug: 'electrician', i18nKey: 'categories.electrician', searchTerm: 'SN_ELECTRICIAN', cover: SENEGAL_PHOTOS.electrician, group: 'categoryGroups.homeRepair' },
  { slug: 'mason', i18nKey: 'categories.mason', searchTerm: 'SN_MASON', cover: SENEGAL_PHOTOS.mason, group: 'categoryGroups.homeRepair' },
  { slug: 'carpenter', i18nKey: 'categories.carpenter', searchTerm: 'SN_CARPENTER', cover: SENEGAL_PHOTOS.carpenter, group: 'categoryGroups.homeRepair' },
  // B. Event services
  { slug: 'photographer', i18nKey: 'categories.photographer', searchTerm: 'SN_PHOTOGRAPHER', cover: SENEGAL_PHOTOS.photographer, group: 'categoryGroups.eventServices' },
  { slug: 'catering', i18nKey: 'categories.catering', searchTerm: 'SN_CATERING', cover: SENEGAL_PHOTOS.catering, group: 'categoryGroups.eventServices' },
  { slug: 'eventDecoration', i18nKey: 'categories.eventDecoration', searchTerm: 'SN_EVENT_DECORATION', cover: SENEGAL_PHOTOS.eventDecoration, group: 'categoryGroups.eventServices' },
  // C. Home services
  { slug: 'houseCleaning', i18nKey: 'categories.houseCleaning', searchTerm: 'SN_HOUSE_CLEANING', cover: SENEGAL_PHOTOS.houseCleaning, group: 'categoryGroups.homeServices' },
  { slug: 'childcare', i18nKey: 'categories.childcare', searchTerm: 'SN_CHILDCARE', cover: SENEGAL_PHOTOS.childcare, group: 'categoryGroups.homeServices' },
  { slug: 'homeCooking', i18nKey: 'categories.homeCooking', searchTerm: 'SN_HOME_COOKING', cover: SENEGAL_PHOTOS.homeCooking, group: 'categoryGroups.homeServices' },
  { slug: 'hairdresser', i18nKey: 'categories.hairdresser', searchTerm: 'SN_HAIRDRESSER', cover: SENEGAL_PHOTOS.hairdresser, group: 'categoryGroups.homeServices' },
  { slug: 'makeupArtist', i18nKey: 'categories.makeupArtist', searchTerm: 'SN_MAKEUP_ARTIST', cover: SENEGAL_PHOTOS.makeupArtist, group: 'categoryGroups.homeServices' },
  { slug: 'manicurist', i18nKey: 'categories.manicurist', searchTerm: 'SN_MANICURIST', cover: SENEGAL_PHOTOS.manicurist, group: 'categoryGroups.homeServices' },
];

/**
 * Per-country featured taxonomy. A market absent from this map falls back
 * to the global {@link MAIN_CATEGORIES} (the European launch set), so adding
 * a future African market is a single entry here + the backend `SN_*`-style
 * rows. Keyed by ISO-3166-1 alpha-2 code.
 */
const CATEGORIES_BY_COUNTRY: Record<string, CuratedCategory[]> = {
  SN: SENEGAL_CATEGORIES,
};

/**
 * Full list of routable curated categories. Used by `CategoryLandingPage`
 * to resolve `/category/:slug` for global, legacy and country-specific slugs.
 */
export const CURATED_CATEGORIES: CuratedCategory[] = [
  ...MAIN_CATEGORIES,
  ...LEGACY_CATEGORIES,
  ...SENEGAL_CATEGORIES,
];

/**
 * Categories rendered on the home page tiles and the landing-page selector.
 *
 * @param countryCode ISO-3166-1 alpha-2 of the visitor's selected market.
 *   When the market has its own taxonomy (e.g. `SN`) that set is returned;
 *   otherwise — and when omitted — the global launch categories are used,
 *   so every European market keeps the exact previous behaviour.
 */
export function getFeaturedCategories(countryCode?: string): CuratedCategory[] {
  const market = countryCode?.toUpperCase();
  if (market && CATEGORIES_BY_COUNTRY[market]) {
    return CATEGORIES_BY_COUNTRY[market];
  }
  return MAIN_CATEGORIES.filter((c) => c.cover);
}

/** Pseudo-random pick from a category's images, stable for a given seed. */
export function pickCategoryImage(slug: CategorySlug, seed = 0): string {
  const list = CATEGORY_IMAGES[slug];
  if (!list || list.length === 0) return '';
  return list[seed % list.length];
}

/**
 * Resolve a specific image file under {@code src/app/asset/<folder>/<file>}
 * to its Vite-hashed URL. Returns empty when the file doesn't exist — the
 * caller can fall back to a random pick from the folder.
 */
function specificImage(folder: string, filename: string): string {
  return modules[`/src/app/asset/${folder}/${filename}`] ?? '';
}

/**
 * Hand-picked photo for each category shown on the home page collage.
 * Values point to specific files in {@code src/app/asset/<folder>/} so we
 * get a consistent, deliberate look instead of the random pick from the
 * folder.
 *
 * To swap one of these for another local file:
 *   1. Drop your image into the matching folder (e.g. {@code asset/hair/}).
 *   2. Update the filename below — Vite auto-reloads.
 *
 * To use an external URL instead (Unsplash, Cloudinary, etc.), just put
 * the full URL in place of the {@code specificImage(...)} call.
 */
export const EDITORIAL_IMAGES: Partial<Record<CategorySlug, string>> = {
  // Beauty & Hair
  beautyHair: specificImage('hair', 'hair11.jpg'),
  // Food & Catering
  foodCatering: specificImage('food', 'food4.jpg'),
  // Artists & Performance — photographer-with-camera shot, picked manually.
  artistsPerformance: specificImage('media_creation', 'media2.jpg'),
  // Fashion & Style
  fashionStyle: specificImage('fashion', 'fashion1.jpg'),
  // Event Services
  eventServices: specificImage('event', 'event2.jpg'),
  // Media, Photo & Animation
  mediaPhotoAnimation: specificImage('media_creation', 'media2.jpg'),
  // Additional Services
  additionalServices: specificImage('makeup', 'makeup4.jpg'),
};

/** Returns the curated URL for a slug, or empty when none is defined. */
export function editorialImage(slug: CategorySlug): string {
  return EDITORIAL_IMAGES[slug] ?? '';
}

/**
 * Two-tier image for the home page collage:
 *   - primary  — curated Unsplash URL (high quality, category-matched)
 *   - fallback — local asset from {@link CATEGORY_IMAGES} for the same slug
 *
 * Consumers should render {@code primary} as the {@code src} and set the
 * fallback via an {@code onError} handler so a broken CDN never leaves the
 * collage with empty frames.
 */
export function getEditorialAccent(slug: CategorySlug, seed = 0): {
  primary: string;
  fallback: string;
} {
  return {
    primary: editorialImage(slug),
    fallback: pickCategoryImage(slug, seed),
  };
}

/** Flat list of every image, useful for inspiration mosaics. */
export function getAllImages(): string[] {
  // De-duplicate URLs because slugs can share folders (e.g. beauty/makeup).
  const set = new Set<string>();
  for (const list of Object.values(folderImages)) {
    for (const url of list) set.add(url);
  }
  return Array.from(set);
}
